// The /alkanes app's single store.
//
// WHY A STORE AND NOT PROPS
//
// Five surfaces read the same four facts — the pool registry, the asset
// registry, the wallet, and which asset is open. Threading those through props
// meant the page root re-rendered on every 20-second pool refresh and dragged
// the chart, the feed and the audited swap panel with it. Here each component
// subscribes to the FIELD it renders, so a pool refresh that leaves an asset's
// price unchanged repaints nothing.
//
// THE ONE RULE FOR CALLERS
//
// Select a field, never the state:
//
//     const assets = useAlkanes((s) => s.assets)        // yes
//     const { assets } = useAlkanes()                    // no — every write
//     const rows = useAlkanes((s) => s.assets.slice(0, 5)) // no — new array
//
// zustand compares selector results with `Object.is`, so a selector that builds
// a new object or array returns a different value on every call: at best it
// re-renders forever, at worst React bails out with a snapshot warning. Every
// derived value this app needs — the open asset's row, its market, the USD
// value of each holding — is therefore computed ONCE in the feed (see
// use-alkanes-feed) and stored as a field, not recomputed inside a selector.

import { create } from 'zustand'
import type { Market, Pool, PoolToken } from './amm'
import type { AssetRow } from './useAssets'
import type { AssetStats } from './useAssetStats'
import type { PoolsHealth } from './usePools'
import type { ActivityEvent } from './useActivity'
import type { PoolChartResponse } from './usePoolChart'
import type { TokenChartResponse } from './useTokenChart'
import { DIESEL_ID, ROUTER_FEE_PER_1000 } from './amm'

/// The app's sections. `wallet` is the canonical profile of ANY address
/// (`/alkanes/profile/:address`); the connected wallet's own profile is that
/// same page at its own address. Social and leaderboard remain reachable by
/// URL and the desktop rail; the phone lives on Home, with the asset's
/// tables and composer under its chart.
export type View = 'home' | 'social' | 'leaderboard' | 'wallet'

export interface BalanceRow {
	token: PoolToken
	raw: bigint
}

export interface AlkanesState {
	// ── the pool registry ────────────────────────────────────────────────────
	pools: Pool[]
	/// SUBFROSTAMM pools (`/alkane/pools` sfamm_pools) — quote-only inputs to
	/// the route engine; never priced, charted or listed like `pools`.
	sfammPools: Pool[]
	tipHeight: number | null
	/// OWRouter's live fee per 1000, as the server read it off chain.
	routerFeePer1000: bigint
	health: PoolsHealth
	updatedAt: number | null
	poolsLoading: boolean
	retryPools: () => void

	// ── the asset registry ───────────────────────────────────────────────────
	assets: AssetRow[]
	assetsLoading: boolean
	btcUsd: number | null

	// ── the open asset ───────────────────────────────────────────────────────
	assetId: string | null
	asset: AssetRow | null
	market: Market | null
	assetStats: AssetStats | null
	assetStatsLoading: boolean

	// ── the open asset's chart ───────────────────────────────────────────────
	/// The all-pools USD index for this asset, and the per-pool reserve series
	/// that stands in when the index does not carry it. Both live here so the
	/// chart component owns drawing and nothing else.
	tokenChart: TokenChartResponse | null
	poolChart: PoolChartResponse | null
	usdCovered: boolean
	chartLoading: boolean
	/// True when the series the chart needs FAILED to fetch and nothing is
	/// drawable — distinct from "loaded fine and empty", which is the only
	/// state allowed to claim "no history".
	chartError: boolean
	/// Refetches whichever chart series failed. Null until the feed mounts.
	retryChart: (() => void) | null

	// ── the wallet ───────────────────────────────────────────────────────────
	loggedIn: boolean
	watchAddress: string | null
	address: string | null
	balances: Map<string, bigint>
	balancesUsd: Map<string, number>
	balanceRows: BalanceRow[]
	balancesLoading: boolean
	/// A current balance request failed. Cached rows may still render, but no
	/// zero/empty claim or trade may be based on them until refresh succeeds.
	balancesUnavailable: boolean
	activity: ActivityEvent[]
	activityLoading: boolean
	activityUnavailable: boolean
	refreshWallet: () => void

	// ── the swap pair ────────────────────────────────────────────────────────
	/// Every tradable token plus the synthetic native-BTC asset, in the order
	/// the swap panel's picker shows them.
	tokens: PoolToken[]
	/// Resolved pair. `sellPick`/`buyPick` are the user's explicit overrides;
	/// these two are what the panel actually trades, defaults folded in.
	sell: PoolToken | null
	buy: PoolToken | null
	sellPick: PoolToken | null
	buyPick: PoolToken | null

	// ── navigation ───────────────────────────────────────────────────────────
	view: View
	/// The address whose public profile the `wallet` view shows. Set only by
	/// `openProfile` / a `/alkanes/profile/:address` URL; null everywhere else.
	profileAddress: string | null

	// ── token verification ───────────────────────────────────────────────────
	/// Server-approved additions to the built-in launch registry. The API is
	/// authoritative; the launch ids remain bundled so an outage cannot remove
	/// an existing trust mark.
	verifiedIds: string[]

	// ── wrap-swap top-up capability ──────────────────────────────────────────
	/// Whether /alkane/wrap-swap accepts `frbtc_outpoints` + `carried_frbtc`
	/// (the partial top-up). Assumed true until a build PROVES otherwise: the
	/// panel verifies the API echoes `swap_amount_in = minted + carried`, and
	/// an older API that silently ignored the fields fails that check — then
	/// this flips for the session and every surface re-plans as a full wrap.
	/// Session-scoped on purpose: the API deploy lands in the same batch, so
	/// a downgrade is a transient compatibility state, not a preference.
	wrapSwapTopup: boolean
	markWrapSwapTopupUnsupported: () => void

	// ── a pending trade intent ───────────────────────────────────────────────
	/// Set by instant-action chips (a position row's "Buy ₿0.001" / "Sell 50%"),
	/// consumed once by the trade surface of the asset it names. It carries an
	/// AMOUNT REQUEST only — the audited quote/review/sign flow runs exactly
	/// as if the amount had been typed. Buys are denominated in BTC, the
	/// entry unit of the trade doors.
	tradeIntent: { assetId: string; side: 'buy' | 'sell'; btc?: number; pct?: number } | null
	setTradeIntent: (intent: AlkanesState['tradeIntent']) => void

	// ── writes ───────────────────────────────────────────────────────────────
	setPools: (
		p: Pick<
			AlkanesState,
			| 'pools'
			| 'sfammPools'
			| 'tipHeight'
			| 'routerFeePer1000'
			| 'health'
			| 'updatedAt'
			| 'poolsLoading'
			| 'retryPools'
		>
	) => void
	setAssets: (p: Pick<AlkanesState, 'assets' | 'assetsLoading' | 'btcUsd'>) => void
	setVerifiedIds: (verifiedIds: string[]) => void
	setOpenAsset: (p: Pick<AlkanesState, 'asset' | 'market'>) => void
	setAssetStats: (p: Pick<AlkanesState, 'assetStats' | 'assetStatsLoading'>) => void
	setChart: (
		p: Pick<
			AlkanesState,
			'tokenChart' | 'poolChart' | 'usdCovered' | 'chartLoading' | 'chartError' | 'retryChart'
		>
	) => void
	setWallet: (
		p: Pick<
			AlkanesState,
			| 'loggedIn'
			| 'watchAddress'
			| 'address'
			| 'balances'
			| 'balancesUsd'
			| 'balanceRows'
			| 'balancesLoading'
			| 'balancesUnavailable'
			| 'refreshWallet'
		>
	) => void
	setActivity: (
		p: Pick<AlkanesState, 'activity' | 'activityLoading' | 'activityUnavailable'>
	) => void
	setPair: (p: Pick<AlkanesState, 'tokens' | 'sell' | 'buy'>) => void

	selectSell: (token: PoolToken) => void
	selectBuy: (token: PoolToken) => void
	flipPair: () => void
	/// Move to a section, optionally opening an asset. Pushes history so the
	/// browser Back button walks the sections a user actually visited — on a
	/// phone that is the way most people leave a screen.
	navigate: (view: View, assetId?: string | null) => void
	/// Open the canonical public profile for an address — its own URL
	/// (`/alkanes/profile/:address`) so any wallet in the app is shareable.
	openProfile: (address: string) => void
	/// Adopt whatever the URL says, for first mount and for `popstate`.
	syncFromLocation: (search: string, pathAsset?: string | null, pathProfile?: string | null) => void
}

const VIEWS = new Set(['home', 'social', 'leaderboard'])

/// Old deep links keep working: the app used to be Discover/Portfolio/
/// Activity/Social, and those URLs are in people's history. The profile-
/// shaped ones (`profile`, `portfolio`, `activity`) are resolved to the
/// canonical `/alkanes/profile/:address` route by `adoptLocation` before
/// they reach here; anything else unknown is Home.
const VIEW_ALIASES: Record<string, View> = {
	discover: 'home'
}

function viewOf(value: string | null): View {
	if (value === null) {
		return 'home'
	}
	if (VIEWS.has(value)) {
		return value as View
	}
	return VIEW_ALIASES[value] ?? 'home'
}

const noop = () => {}

export const useAlkanes = create<AlkanesState>((set, get) => ({
	pools: [],
	sfammPools: [],
	tipHeight: null,
	routerFeePer1000: ROUTER_FEE_PER_1000,
	health: 'ok',
	updatedAt: null,
	poolsLoading: true,
	retryPools: noop,

	assets: [],
	assetsLoading: true,
	btcUsd: null,

	// Home is a market, not a second token-directory screen. DIESEL is the
	// canonical market whenever a Home URL does not name another asset.
	assetId: DIESEL_ID,
	asset: null,
	market: null,
	assetStats: null,
	assetStatsLoading: false,

	tokenChart: null,
	poolChart: null,
	usdCovered: false,
	chartLoading: true,
	chartError: false,
	retryChart: null,

	loggedIn: false,
	watchAddress: null,
	address: null,
	balances: new Map(),
	balancesUsd: new Map(),
	balanceRows: [],
	balancesLoading: false,
	balancesUnavailable: false,
	activity: [],
	activityLoading: false,
	activityUnavailable: false,
	refreshWallet: noop,

	tokens: [],
	sell: null,
	buy: null,
	sellPick: null,
	buyPick: null,

	view: 'home',
	profileAddress: null,
	verifiedIds: [],

	wrapSwapTopup: true,
	markWrapSwapTopupUnsupported: () => set({ wrapSwapTopup: false }),

	tradeIntent: null,
	setTradeIntent: (intent) => set({ tradeIntent: intent }),

	setPools: (p) => set(p),
	setAssets: (p) => set(p),
	setVerifiedIds: (verifiedIds) => set({ verifiedIds }),
	setOpenAsset: (p) => set(p),
	setAssetStats: (p) => set(p),
	setChart: (p) => set(p),
	setWallet: (p) => set(p),
	setActivity: (p) => set(p),
	setPair: (p) => set(p),

	selectSell: (token) => {
		// Picking the token you already hold on the other side would leave the
		// panel quoting an asset against itself; swap them instead of refusing.
		const { buyPick, buy } = get()
		if ((buyPick ?? buy)?.id === token.id) {
			set({ buyPick: get().sell })
		}
		set({ sellPick: token })
	},
	selectBuy: (token) => {
		const { sellPick, sell } = get()
		if ((sellPick ?? sell)?.id === token.id) {
			set({ sellPick: get().buy })
		}
		set({ buyPick: token })
	},
	flipPair: () => set({ sellPick: get().buy, buyPick: get().sell }),

	navigate: (view, assetId) => {
		const requestedAsset = assetId === undefined ? get().assetId : assetId
		const nextAsset = view === 'home' ? requestedAsset || DIESEL_ID : requestedAsset
		// A fresh asset resets the pair, so the panel always opens on "buy this
		// with BTC" rather than on the last thing that happened to be traded.
		const changedAsset = nextAsset !== get().assetId
		set({
			view,
			assetId: nextAsset,
			profileAddress: null,
			...(changedAsset ? { sellPick: null, buyPick: null } : {})
		})
		if (typeof window === 'undefined') {
			return
		}
		const current = new URLSearchParams(window.location.search)
		const params = new URLSearchParams()
		const watch = current.get('watch')
		if (watch) params.set('watch', watch)
		const query = params.size ? `?${params.toString()}` : ''
		const pathname =
			view === 'home' && nextAsset
				? `/alkanes/${nextAsset}`
				: `/alkanes?view=${encodeURIComponent(view)}${query ? `&${params.toString()}` : ''}`
		window.history.pushState(null, '', view === 'home' ? `${pathname}${query}` : pathname)
	},

	openProfile: (address) => {
		set({ view: 'wallet', profileAddress: address })
		if (typeof window === 'undefined') {
			return
		}
		window.history.pushState(null, '', `/alkanes/profile/${encodeURIComponent(address)}`)
	},

	syncFromLocation: (search, pathAsset = null, pathProfile = null) => {
		// A profile pathname IS the section — no query can override it.
		if (pathProfile) {
			set({ view: 'wallet', profileAddress: pathProfile })
			return
		}
		const params = new URLSearchParams(search)
		// Explicit sections beat an asset pathname. Browsers can retain a mixed
		// URL such as `/alkanes/2:0?view=social` during route migrations; letting
		// the pathname win hid the composer even though the URL still said Social.
		const requestedView = params.get('view')
		const view = requestedView === null && pathAsset ? 'home' : viewOf(requestedView)
		const requestedAsset = pathAsset ?? params.get('asset')
		set({
			view,
			profileAddress: null,
			assetId: view === 'home' ? requestedAsset || DIESEL_ID : requestedAsset
		})
	}
}))
